import type { HdrMode, MediaType, MetaPreview, Settings, StremioStream } from '../types'
import type { CodecCapabilities } from './codecs'
import type { SyncSnapshot } from './syncTypes'

export const REMOTE_PROTOCOL_VERSION = 1 as const
export const REMOTE_MESSAGE_MAX_BYTES = 262_144

export type RemoteSection = 'home' | 'movies' | 'series' | 'search' | 'library' | 'settings'
export type RemoteScreen = RemoteSection | 'details' | 'sources' | 'resolving' | 'player' | 'trailer' | 'setup'

export interface RemoteMediaRef {
  id: string
  type: MediaType
  addonUrl?: string
}

export type RemoteKey =
  | 'up' | 'down' | 'left' | 'right' | 'ok' | 'back' | 'home' | 'menu' | 'info'
  | 'red' | 'green' | 'yellow' | 'blue'
  | 'play' | 'pause' | 'stop' | 'rewind' | 'fast_forward'

export type RemotePlayerCommand =
  | { kind: 'play' | 'pause' | 'toggle' | 'stop' | 'next' | 'previous' }
  | { kind: 'seek_by'; seconds: number }
  | { kind: 'seek_to'; seconds: number }
  | { kind: 'subtitle'; id: string | null }
  | { kind: 'audio'; index: number }
  | { kind: 'speed'; rate: number }
  | { kind: 'picture'; mode: 'fit' | 'fill' | 'zoom' }
  | { kind: 'sleep'; value: 0 | 15 | 30 | 60 | 'end' }

export type RemoteSettingsPatch = Partial<Pick<Settings,
  | 'serverUrl' | 'torrentioUrl' | 'torrentioEnabled' | 'autoplayNext'
  | 'preferHls' | 'subtitlesEnabled' | 'subtitleLanguage' | 'subtitleSize'
  | 'subtitlesUrl' | 'hdrMode' | 'addons' | 'subtitleDelay' | 'subtitlePosition'
  | 'subtitleBackground' | 'subtitleSdh' | 'subtitleForced' | 'subtitleEncoding'
  | 'preferredAudioLanguage' | 'restorePlayback' | 'oledProtection' | 'addonsConfigured'
>>

export type RemoteCommand =
  | { kind: 'navigate'; section: RemoteSection }
  | { kind: 'search'; query: string }
  | { kind: 'open_title'; media: RemoteMediaRef }
  | { kind: 'open_sources'; media: RemoteMediaRef; videoId?: string }
  | { kind: 'play_title'; media: RemoteMediaRef; videoId?: string }
  | { kind: 'play_source'; media: RemoteMediaRef; sourceKey: string; videoId?: string }
  | { kind: 'toggle_library'; media: RemoteMediaRef }
  | { kind: 'update_settings'; settings: RemoteSettingsPatch }
  | { kind: 'apply_sync'; snapshot: SyncSnapshot; mode: 'merge' | 'replace' }
  | { kind: 'key'; key: RemoteKey }
  | { kind: 'player'; action: RemotePlayerCommand }

export interface RemoteMediaSummary extends RemoteMediaRef {
  name: string
  poster?: string
  background?: string
  description?: string
  releaseInfo?: string
  imdbRating?: string
}

export interface RemoteChoice {
  id: string
  label: string
  detail?: string
  active?: boolean
}

export interface RemoteEpisodeChoice extends RemoteChoice {
  season?: number
  episode?: number
}

export interface RemoteHdrState {
  source: '' | 'HDR10' | 'HDR10+' | 'Dolby Vision'
  capability: 'supported' | 'unsupported' | 'unknown'
  display: 'high' | 'standard' | 'unknown'
  playing: boolean
  note: string
}

export interface RemotePlayerState {
  status: 'preparing' | 'buffering' | 'playing' | 'paused' | 'error'
  media: RemoteMediaSummary
  videoId?: string
  title: string
  label: string
  currentTime: number
  duration: number
  buffered: number
  playing: boolean
  error?: string
  subtitle?: string
  subtitles: RemoteChoice[]
  audio: RemoteChoice[]
  episodes: RemoteEpisodeChoice[]
  speed: number
  picture: 'fit' | 'fill' | 'zoom'
  sleep: 0 | 15 | 30 | 60 | 'end'
  hdr: RemoteHdrState
}

export interface RemoteTvState {
  v: typeof REMOTE_PROTOCOL_VERSION
  revision: number
  updatedAt: number
  screen: RemoteScreen
  section: RemoteSection
  selected?: RemoteMediaSummary
  selectedInLibrary?: boolean
  library?: RemoteMediaRef[]
  progress?: Array<{
    media: RemoteMediaRef
    videoId?: string
    currentTime: number
    duration: number
    updatedAt: number
  }>
  resolving?: string
  player?: RemotePlayerState
  hdrCapabilities: {
    display: 'high' | 'standard' | 'unknown'
    hdr10: 'supported' | 'unsupported' | 'unknown'
    hdr10Plus: 'supported' | 'unsupported' | 'unknown'
    dolbyVision: 'supported' | 'unsupported' | 'unknown'
    source: string
  }
  sourceContext?: {
    addonUrl: string
    enabled: boolean
    measuredSpeed?: number
    hdrMode: HdrMode
    codecCapabilities: CodecCapabilities
  }
  settings?: Settings
  /** Full private sync data is relayed only to authenticated paired phones and
   * is not persisted by the Worker. */
  sync?: SyncSnapshot
}

export interface RemoteCommandEnvelope {
  v: typeof REMOTE_PROTOCOL_VERSION
  type: 'command'
  id: string
  command: RemoteCommand
  sentAt: number
}

export type ClientRemoteMessage =
  | RemoteCommandEnvelope
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'state'; state: RemoteTvState }
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'state_request' }

export type ServerRemoteMessage =
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'hello'; role: 'tv' | 'controller'; tvOnline: boolean; controllers: number }
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'presence'; tvOnline: boolean; controllers: number }
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'state'; state: RemoteTvState }
  | RemoteCommandEnvelope
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'state_request' }
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'ack'; id: string; status: 'delivered' | 'queued' | 'rejected'; message?: string }
  | { v: typeof REMOTE_PROTOCOL_VERSION; type: 'error'; message: string }

const SECTIONS: RemoteSection[] = ['home', 'movies', 'series', 'search', 'library', 'settings']
const SCREENS: RemoteScreen[] = [...SECTIONS, 'details', 'sources', 'resolving', 'player', 'trailer', 'setup']
const KEYS: RemoteKey[] = [
  'up', 'down', 'left', 'right', 'ok', 'back', 'home', 'menu', 'info',
  'red', 'green', 'yellow', 'blue', 'play', 'pause', 'stop', 'rewind', 'fast_forward',
]

function record(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function shortText(value: unknown, maximum: number, allowEmpty = false): value is string {
  return typeof value === 'string' && value.length <= maximum && (allowEmpty || value.length > 0)
}

function finiteNumber(value: unknown, minimum: number, maximum: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum
}

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[]) {
  return Object.keys(value).every((key) => allowed.includes(key))
}

const SETTINGS_KEYS = [
  'serverUrl', 'torrentioUrl', 'torrentioEnabled', 'autoplayNext', 'preferHls',
  'subtitlesEnabled', 'subtitleLanguage', 'subtitleSize', 'subtitlesUrl', 'hdrMode',
  'addons', 'subtitleDelay', 'subtitlePosition', 'subtitleBackground', 'subtitleSdh',
  'subtitleForced', 'subtitleEncoding', 'preferredAudioLanguage', 'restorePlayback', 'oledProtection', 'addonsConfigured',
] as const

function httpUrl(value: unknown, maximum = 500) {
  if (!shortText(value, maximum)) return false
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch { return false }
}

export function boundedRemoteText(value: unknown, maximum: number, fallback = '') {
  return typeof value === 'string' && value.length ? value.slice(0, maximum) : fallback.slice(0, maximum)
}

export function boundedRemoteNumber(value: unknown, minimum: number, maximum: number, fallback = minimum) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(minimum, Math.min(maximum, value))
    : Math.max(minimum, Math.min(maximum, fallback))
}

export function toRemoteMediaRef(meta: MetaPreview): RemoteMediaRef {
  return {
    id: boundedRemoteText(meta.id, 240, 'unknown'),
    type: meta.type === 'series' ? 'series' : 'movie',
    addonUrl: httpUrl(meta.addonUrl) ? meta.addonUrl : undefined,
  }
}

/** Keep untrusted add-on metadata inside the smaller live-controller budget,
 * not merely inside the protocol's absolute rejection limits. */
export function toRemoteMediaSummary(meta: MetaPreview): RemoteMediaSummary {
  return {
    ...toRemoteMediaRef(meta),
    name: boundedRemoteText(meta.name, 200, 'Untitled'),
    poster: httpUrl(meta.poster, 800) ? meta.poster : undefined,
    background: httpUrl(meta.background, 800) ? meta.background : undefined,
    description: meta.description === undefined ? undefined : boundedRemoteText(meta.description, 800),
    releaseInfo: meta.releaseInfo !== undefined
      ? boundedRemoteText(meta.releaseInfo, 80)
      : meta.year !== undefined ? boundedRemoteText(String(meta.year), 20) : undefined,
    imdbRating: meta.imdbRating === undefined ? undefined : boundedRemoteText(meta.imdbRating, 20),
  }
}

function isRemoteSettingsPatch(value: unknown): value is RemoteSettingsPatch {
  if (!record(value)) return false
  const keys = Object.keys(value)
  if (!keys.length || keys.some((key) => !(SETTINGS_KEYS as readonly string[]).includes(key))) return false
  if (value.serverUrl !== undefined && !httpUrl(value.serverUrl)) return false
  if (value.torrentioUrl !== undefined && !httpUrl(value.torrentioUrl)) return false
  if (value.subtitlesUrl !== undefined && !httpUrl(value.subtitlesUrl)) return false
  for (const key of ['torrentioEnabled', 'autoplayNext', 'preferHls', 'subtitlesEnabled', 'restorePlayback', 'oledProtection', 'addonsConfigured'] as const) {
    if (value[key] !== undefined && typeof value[key] !== 'boolean') return false
  }
  if (value.subtitleLanguage !== undefined && !shortText(value.subtitleLanguage, 12)) return false
  if (value.preferredAudioLanguage !== undefined && !shortText(value.preferredAudioLanguage, 12)) return false
  if (value.subtitleSize !== undefined && !['small', 'medium', 'large'].includes(value.subtitleSize as string)) return false
  if (value.hdrMode !== undefined && !['auto', 'prefer', 'sdr'].includes(value.hdrMode as string)) return false
  if (value.subtitleDelay !== undefined && !finiteNumber(value.subtitleDelay, -30, 30)) return false
  if (value.subtitlePosition !== undefined && !['bottom', 'middle', 'top'].includes(value.subtitlePosition as string)) return false
  if (value.subtitleBackground !== undefined && !['none', 'shadow', 'box'].includes(value.subtitleBackground as string)) return false
  if (value.subtitleSdh !== undefined && !['include', 'prefer', 'exclude'].includes(value.subtitleSdh as string)) return false
  if (value.subtitleForced !== undefined && !['include', 'prefer', 'only'].includes(value.subtitleForced as string)) return false
  if (value.subtitleEncoding !== undefined && !['auto', 'utf-8', 'windows-1252'].includes(value.subtitleEncoding as string)) return false
  if (value.addons !== undefined && (!Array.isArray(value.addons) || value.addons.length > 12 || !value.addons.every((addon, index) => (
    record(addon)
      && onlyKeys(addon, ['id', 'name', 'url', 'enabled', 'priority', 'resources'])
      && shortText(addon.id, 80)
      && shortText(addon.name, 80)
      && httpUrl(addon.url)
      && typeof addon.enabled === 'boolean'
      && Number.isInteger(addon.priority) && addon.priority === index
      && (addon.resources === undefined || (Array.isArray(addon.resources) && addon.resources.length <= 4 && addon.resources.every((resource) => ['catalog', 'meta', 'stream', 'subtitles'].includes(resource as string))))
  )))) return false
  return true
}

const META_KEYS = [
  'id', 'imdb_id', 'type', 'name', 'poster', 'background', 'logo', 'description',
  'releaseInfo', 'year', 'imdbRating', 'runtime', 'genre', 'genres', 'released', 'addonUrl',
] as const

function stringList(value: unknown, maximumItems: number, maximumLength: number) {
  return Array.isArray(value) && value.length <= maximumItems
    && value.every((item) => shortText(item, maximumLength))
}

function isRemoteMeta(value: unknown) {
  if (!record(value) || !shortText(value.id, 240) || !shortText(value.name, 200)
    || (value.type !== 'movie' && value.type !== 'series') || !onlyKeys(value, META_KEYS)) return false
  for (const key of ['poster', 'background', 'logo'] as const) {
    if (value[key] !== undefined && !httpUrl(value[key], 800)) return false
  }
  if (value.description !== undefined && !shortText(value.description, 800, true)) return false
  for (const [key, maximum] of [['imdb_id', 240], ['releaseInfo', 80], ['imdbRating', 20], ['runtime', 40], ['released', 80]] as const) {
    if (value[key] !== undefined && !shortText(value[key], maximum, true)) return false
  }
  if (value.year !== undefined && !(shortText(value.year, 20, true) || finiteNumber(value.year, 0, 9999))) return false
  if (value.genre !== undefined && !stringList(value.genre, 12, 40)) return false
  if (value.genres !== undefined && !stringList(value.genres, 12, 40)) return false
  if (value.addonUrl !== undefined && !httpUrl(value.addonUrl)) return false
  return true
}

const VIDEO_KEYS = [
  'id', 'title', 'name', 'season', 'episode', 'released', 'thumbnail', 'overview',
  'chapters', 'introStart', 'introEnd', 'creditsStart',
] as const

function isSyncVideo(value: unknown) {
  if (!record(value) || !onlyKeys(value, VIDEO_KEYS) || !shortText(value.id, 240)) return false
  for (const key of ['title', 'name'] as const) {
    if (value[key] !== undefined && !shortText(value[key], 200, true)) return false
  }
  if (value.released !== undefined && !shortText(value.released, 80, true)) return false
  if (value.thumbnail !== undefined && !httpUrl(value.thumbnail, 800)) return false
  if (value.overview !== undefined && !shortText(value.overview, 1_200, true)) return false
  for (const key of ['season', 'episode'] as const) {
    if (value[key] !== undefined && (!Number.isInteger(value[key]) || !finiteNumber(value[key], 0, 100_000))) return false
  }
  for (const key of ['introStart', 'introEnd', 'creditsStart'] as const) {
    if (value[key] !== undefined && !finiteNumber(value[key], 0, 86_400)) return false
  }
  if (value.chapters !== undefined && (!Array.isArray(value.chapters) || value.chapters.length > 100 || !value.chapters.every((chapter) => (
    record(chapter) && onlyKeys(chapter, ['title', 'start']) && shortText(chapter.title, 200) && finiteNumber(chapter.start, 0, 86_400)
  )))) return false
  return true
}

const SYNC_PREFERENCE_KEYS = [
  'subtitleLanguage', 'subtitleSize', 'subtitleDelay', 'subtitlePosition',
  'subtitleBackground', 'subtitleSdh', 'subtitleForced', 'subtitleEncoding',
  'preferredAudioLanguage', 'hdrMode', 'autoplayNext',
] as const

function isSyncPreferences(value: unknown) {
  if (!record(value) || !onlyKeys(value, SYNC_PREFERENCE_KEYS)) return false
  if (!shortText(value.subtitleLanguage, 12) || !shortText(value.preferredAudioLanguage, 12)) return false
  if (!['small', 'medium', 'large'].includes(value.subtitleSize as string)) return false
  if (!finiteNumber(value.subtitleDelay, -30, 30)) return false
  if (!['bottom', 'middle', 'top'].includes(value.subtitlePosition as string)) return false
  if (!['none', 'shadow', 'box'].includes(value.subtitleBackground as string)) return false
  if (!['include', 'prefer', 'exclude'].includes(value.subtitleSdh as string)) return false
  if (!['include', 'prefer', 'only'].includes(value.subtitleForced as string)) return false
  if (!['auto', 'utf-8', 'windows-1252'].includes(value.subtitleEncoding as string)) return false
  if (!['auto', 'prefer', 'sdr'].includes(value.hdrMode as string)) return false
  return typeof value.autoplayNext === 'boolean'
}

export function isSyncSnapshot(value: unknown, maximumLibrary = 60): value is SyncSnapshot {
  if (!record(value) || value.version !== 1 || !finiteNumber(value.exportedAt, 0, 9_999_999_999_999)
    || !Array.isArray(value.library) || value.library.length > maximumLibrary || !value.library.every(isRemoteMeta)
    || !Array.isArray(value.progress) || value.progress.length > 30 || !record(value.watched)
    || Object.keys(value.watched).length > 300 || !isSyncPreferences(value.preferences)
    || !onlyKeys(value, ['version', 'exportedAt', 'library', 'progress', 'watched', 'preferences'])) return false
  if (!value.progress.every((entry) => record(entry) && shortText(entry.key, 300) && isRemoteMeta(entry.meta)
    && onlyKeys(entry, ['key', 'meta', 'video', 'currentTime', 'duration', 'updatedAt'])
    && (entry.video === undefined || isSyncVideo(entry.video))
    && finiteNumber(entry.currentTime, 0, 86_400) && finiteNumber(entry.duration, 0, 86_400)
    && finiteNumber(entry.updatedAt, 0, 9_999_999_999_999))) return false
  if (!Object.entries(value.watched).every(([key, timestamp]) => key.length > 0 && key.length <= 300
    && finiteNumber(timestamp, 1, 9_999_999_999_999))) return false
  return true
}

export function isRemoteMediaRef(value: unknown): value is RemoteMediaRef {
  if (!record(value)) return false
  return (value.type === 'movie' || value.type === 'series')
    && shortText(value.id, 240)
    && (value.addonUrl === undefined || httpUrl(value.addonUrl))
    && onlyKeys(value, ['id', 'type', 'addonUrl'])
}

export function isRemotePlayerCommand(value: unknown): value is RemotePlayerCommand {
  if (!record(value) || !shortText(value.kind, 32)) return false
  if (['play', 'pause', 'toggle', 'stop', 'next', 'previous'].includes(value.kind)) return onlyKeys(value, ['kind'])
  if (value.kind === 'seek_by') return onlyKeys(value, ['kind', 'seconds']) && finiteNumber(value.seconds, -3600, 3600) && value.seconds !== 0
  if (value.kind === 'seek_to') return onlyKeys(value, ['kind', 'seconds']) && finiteNumber(value.seconds, 0, 86_400)
  if (value.kind === 'subtitle') return onlyKeys(value, ['kind', 'id']) && (value.id === null || shortText(value.id, 300))
  if (value.kind === 'audio') return onlyKeys(value, ['kind', 'index']) && Number.isInteger(value.index) && finiteNumber(value.index, 0, 99)
  if (value.kind === 'speed') return onlyKeys(value, ['kind', 'rate']) && [0.75, 1, 1.25, 1.5].includes(value.rate as number)
  if (value.kind === 'picture') return onlyKeys(value, ['kind', 'mode']) && ['fit', 'fill', 'zoom'].includes(value.mode as string)
  if (value.kind === 'sleep') return onlyKeys(value, ['kind', 'value']) && [0, 15, 30, 60, 'end'].includes(value.value as number | string)
  return false
}

export function isRemoteCommand(value: unknown): value is RemoteCommand {
  if (!record(value) || !shortText(value.kind, 32)) return false
  if (value.kind === 'navigate') return onlyKeys(value, ['kind', 'section']) && SECTIONS.includes(value.section as RemoteSection)
  if (value.kind === 'search') return onlyKeys(value, ['kind', 'query']) && shortText(value.query, 120)
  if (value.kind === 'open_title' || value.kind === 'toggle_library') return onlyKeys(value, ['kind', 'media']) && isRemoteMediaRef(value.media)
  if (value.kind === 'update_settings') return onlyKeys(value, ['kind', 'settings']) && isRemoteSettingsPatch(value.settings)
  if (value.kind === 'apply_sync') return onlyKeys(value, ['kind', 'snapshot', 'mode']) && (value.mode === 'merge' || value.mode === 'replace') && isSyncSnapshot(value.snapshot)
  if (value.kind === 'open_sources' || value.kind === 'play_title') {
    return onlyKeys(value, ['kind', 'media', 'videoId']) && isRemoteMediaRef(value.media) && (value.videoId === undefined || shortText(value.videoId, 240))
  }
  if (value.kind === 'play_source') {
    return onlyKeys(value, ['kind', 'media', 'sourceKey', 'videoId'])
      && isRemoteMediaRef(value.media)
      && shortText(value.sourceKey, 1_200)
      && (value.videoId === undefined || shortText(value.videoId, 240))
  }
  if (value.kind === 'key') return onlyKeys(value, ['kind', 'key']) && KEYS.includes(value.key as RemoteKey)
  if (value.kind === 'player') return onlyKeys(value, ['kind', 'action']) && isRemotePlayerCommand(value.action)
  return false
}

export function isRemoteCommandEnvelope(value: unknown): value is RemoteCommandEnvelope {
  if (!record(value)) return false
  return value.v === REMOTE_PROTOCOL_VERSION
    && value.type === 'command'
    && onlyKeys(value, ['v', 'type', 'id', 'command', 'sentAt'])
    && shortText(value.id, 120)
    && finiteNumber(value.sentAt, 0, 9_999_999_999_999)
    && isRemoteCommand(value.command)
}

function isRemoteMediaSummary(value: unknown): value is RemoteMediaSummary {
  if (!record(value) || !onlyKeys(value, [
    'id', 'type', 'addonUrl', 'name', 'poster', 'background', 'description', 'releaseInfo', 'imdbRating',
  ])) return false
  if ((value.type !== 'movie' && value.type !== 'series') || !shortText(value.id, 240) || !shortText(value.name, 300)) return false
  if (value.addonUrl !== undefined && !httpUrl(value.addonUrl)) return false
  for (const key of ['poster', 'background'] as const) {
    if (value[key] !== undefined && !httpUrl(value[key], 2_000)) return false
  }
  if (value.description !== undefined && !shortText(value.description, 2_000, true)) return false
  if (value.releaseInfo !== undefined && !shortText(value.releaseInfo, 120, true)) return false
  return value.imdbRating === undefined || shortText(value.imdbRating, 24, true)
}

function isRemoteChoice(value: unknown, episode = false) {
  if (!record(value) || !onlyKeys(value, episode
    ? ['id', 'label', 'detail', 'active', 'season', 'episode']
    : ['id', 'label', 'detail', 'active'])) return false
  if (!shortText(value.id, 300) || !shortText(value.label, 300)) return false
  if (value.detail !== undefined && !shortText(value.detail, 500, true)) return false
  if (value.active !== undefined && typeof value.active !== 'boolean') return false
  if (!episode) return true
  for (const key of ['season', 'episode'] as const) {
    if (value[key] !== undefined && (!Number.isInteger(value[key]) || !finiteNumber(value[key], 0, 100_000))) return false
  }
  return true
}

function isRemoteHdrState(value: unknown) {
  return record(value)
    && onlyKeys(value, ['source', 'capability', 'display', 'playing', 'note'])
    && ['', 'HDR10', 'HDR10+', 'Dolby Vision'].includes(value.source as string)
    && ['supported', 'unsupported', 'unknown'].includes(value.capability as string)
    && ['high', 'standard', 'unknown'].includes(value.display as string)
    && typeof value.playing === 'boolean'
    && shortText(value.note, 500, true)
}

function isRemotePlayerState(value: unknown): value is RemotePlayerState {
  if (!record(value) || !onlyKeys(value, [
    'status', 'media', 'videoId', 'title', 'label', 'currentTime', 'duration', 'buffered', 'playing', 'error',
    'subtitle', 'subtitles', 'audio', 'episodes', 'speed', 'picture', 'sleep', 'hdr',
  ])) return false
  if (!['preparing', 'buffering', 'playing', 'paused', 'error'].includes(value.status as string)
    || !isRemoteMediaSummary(value.media)
    || (value.videoId !== undefined && !shortText(value.videoId, 240))
    || !shortText(value.title, 300) || !shortText(value.label, 120)
    || !finiteNumber(value.currentTime, 0, 86_400) || !finiteNumber(value.duration, 0, 86_400)
    || !finiteNumber(value.buffered, 0, 1) || typeof value.playing !== 'boolean'
    || (value.error !== undefined && !shortText(value.error, 500, true))
    || (value.subtitle !== undefined && !shortText(value.subtitle, 120, true))) return false
  if (!Array.isArray(value.subtitles) || value.subtitles.length > 64 || !value.subtitles.every((entry) => isRemoteChoice(entry))) return false
  if (!Array.isArray(value.audio) || value.audio.length > 24 || !value.audio.every((entry) => isRemoteChoice(entry))) return false
  if (!Array.isArray(value.episodes) || value.episodes.length > 100 || !value.episodes.every((entry) => isRemoteChoice(entry, true))) return false
  return [0.75, 1, 1.25, 1.5].includes(value.speed as number)
    && ['fit', 'fill', 'zoom'].includes(value.picture as string)
    && [0, 15, 30, 60, 'end'].includes(value.sleep as number | string)
    && isRemoteHdrState(value.hdr)
}

const CODEC_KEYS = ['h264', 'hevc', 'vp9', 'av1', 'aac', 'ac3', 'eac3', 'opus', 'hls'] as const

function isCodecCapabilities(value: unknown) {
  if (!record(value) || !onlyKeys(value, [...CODEC_KEYS, 'source', 'testedAt'])) return false
  if (!CODEC_KEYS.every((key) => ['supported', 'maybe', 'unsupported', 'unknown'].includes(value[key] as string))) return false
  return shortText(value.source, 200) && finiteNumber(value.testedAt, 0, 9_999_999_999_999)
}

function isHdrCapabilities(value: unknown) {
  return record(value)
    && onlyKeys(value, ['display', 'hdr10', 'hdr10Plus', 'dolbyVision', 'source'])
    && ['high', 'standard', 'unknown'].includes(value.display as string)
    && ['supported', 'unsupported', 'unknown'].includes(value.hdr10 as string)
    && ['supported', 'unsupported', 'unknown'].includes(value.hdr10Plus as string)
    && ['supported', 'unsupported', 'unknown'].includes(value.dolbyVision as string)
    && shortText(value.source, 200)
}

export function isRemoteTvState(value: unknown): value is RemoteTvState {
  if (!record(value) || value.v !== REMOTE_PROTOCOL_VERSION) return false
  if (!finiteNumber(value.revision, 0, Number.MAX_SAFE_INTEGER) || !finiteNumber(value.updatedAt, 0, 9_999_999_999_999)) return false
  if (!SCREENS.includes(value.screen as RemoteScreen) || !SECTIONS.includes(value.section as RemoteSection)) return false
  if (!onlyKeys(value, [
    'v', 'revision', 'updatedAt', 'screen', 'section', 'selected', 'selectedInLibrary', 'library', 'progress',
    'resolving', 'player', 'hdrCapabilities', 'sourceContext', 'settings', 'sync',
  ])) return false
  if (value.selected !== undefined && !isRemoteMediaSummary(value.selected)) return false
  if (value.selectedInLibrary !== undefined && typeof value.selectedInLibrary !== 'boolean') return false
  if (value.library !== undefined && (!Array.isArray(value.library) || value.library.length > 100 || !value.library.every(isRemoteMediaRef))) return false
  if (value.progress !== undefined && (!Array.isArray(value.progress) || value.progress.length > 30 || !value.progress.every((entry) => (
    record(entry) && onlyKeys(entry, ['media', 'videoId', 'currentTime', 'duration', 'updatedAt']) && isRemoteMediaRef(entry.media)
      && (entry.videoId === undefined || shortText(entry.videoId, 240))
      && finiteNumber(entry.currentTime, 0, 86_400)
      && finiteNumber(entry.duration, 0, 86_400)
      && finiteNumber(entry.updatedAt, 0, 9_999_999_999_999)
  )))) return false
  if (value.resolving !== undefined && !shortText(value.resolving, 300, true)) return false
  if (value.player !== undefined && !isRemotePlayerState(value.player)) return false
  if (!isHdrCapabilities(value.hdrCapabilities)) return false
  if (value.sourceContext !== undefined && (!record(value.sourceContext)
    || !onlyKeys(value.sourceContext, ['addonUrl', 'enabled', 'measuredSpeed', 'hdrMode', 'codecCapabilities'])
    || !httpUrl(value.sourceContext.addonUrl)
    || typeof value.sourceContext.enabled !== 'boolean'
    || !['auto', 'prefer', 'sdr'].includes(value.sourceContext.hdrMode as string)
    || (value.sourceContext.measuredSpeed !== undefined && !finiteNumber(value.sourceContext.measuredSpeed, 0, 10_000_000_000))
    || !isCodecCapabilities(value.sourceContext.codecCapabilities))) return false
  if (value.settings !== undefined && !isRemoteSettingsPatch(value.settings)) return false
  if (value.sync !== undefined && !isSyncSnapshot(value.sync)) return false
  return true
}

export function parseClientRemoteMessage(raw: string): ClientRemoteMessage | null {
  if (raw.length > REMOTE_MESSAGE_MAX_BYTES || new TextEncoder().encode(raw).byteLength > REMOTE_MESSAGE_MAX_BYTES) return null
  let value: unknown
  try { value = JSON.parse(raw) } catch { return null }
  if (!record(value) || value.v !== REMOTE_PROTOCOL_VERSION) return null
  if (value.type === 'command') return isRemoteCommandEnvelope(value) ? value : null
  if (value.type === 'state_request') return onlyKeys(value, ['v', 'type']) ? { v: REMOTE_PROTOCOL_VERSION, type: 'state_request' } : null
  if (value.type === 'state' && onlyKeys(value, ['v', 'type', 'state']) && isRemoteTvState(value.state)) return value as unknown as ClientRemoteMessage
  return null
}

export function parseServerRemoteMessage(raw: string): ServerRemoteMessage | null {
  if (raw.length > REMOTE_MESSAGE_MAX_BYTES || new TextEncoder().encode(raw).byteLength > REMOTE_MESSAGE_MAX_BYTES) return null
  let value: unknown
  try { value = JSON.parse(raw) } catch { return null }
  if (!record(value) || value.v !== REMOTE_PROTOCOL_VERSION || !shortText(value.type, 32)) return null
  if (value.type === 'command') return isRemoteCommandEnvelope(value) ? value : null
  if (value.type === 'state') return onlyKeys(value, ['v', 'type', 'state']) && isRemoteTvState(value.state) ? value as unknown as ServerRemoteMessage : null
  if (value.type === 'state_request') return onlyKeys(value, ['v', 'type']) ? { v: REMOTE_PROTOCOL_VERSION, type: 'state_request' } : null
  if (value.type === 'hello') {
    return onlyKeys(value, ['v', 'type', 'role', 'tvOnline', 'controllers'])
      && (value.role === 'tv' || value.role === 'controller')
      && typeof value.tvOnline === 'boolean'
      && Number.isInteger(value.controllers) && finiteNumber(value.controllers, 0, 100)
      ? value as unknown as ServerRemoteMessage : null
  }
  if (value.type === 'presence') {
    return onlyKeys(value, ['v', 'type', 'tvOnline', 'controllers'])
      && typeof value.tvOnline === 'boolean' && Number.isInteger(value.controllers) && finiteNumber(value.controllers, 0, 100)
      ? value as unknown as ServerRemoteMessage : null
  }
  if (value.type === 'ack') {
    return onlyKeys(value, ['v', 'type', 'id', 'status', 'message'])
      && shortText(value.id, 120) && ['delivered', 'queued', 'rejected'].includes(value.status as string)
      && (value.message === undefined || shortText(value.message, 500))
      ? value as unknown as ServerRemoteMessage : null
  }
  if (value.type === 'error') return onlyKeys(value, ['v', 'type', 'message']) && shortText(value.message, 500) ? value as unknown as ServerRemoteMessage : null
  return null
}

export function streamRemoteKey(stream: StremioStream) {
  if (stream.infoHash && /^[a-f0-9]{40}$/i.test(stream.infoHash)) {
    const file = Number.isInteger(stream.fileIdx)
      ? String(stream.fileIdx)
      : sourceFingerprint(JSON.stringify([
        stream.fileMustInclude || [],
        stream.behaviorHints?.filename || '',
      ]))
    return `torrent:${stream.infoHash.toLowerCase()}:${file}`
  }
  if (stream.url) return `url:${sourceFingerprint(directStreamIdentity(stream.url, stream))}`
  if (stream.externalUrl) return `external:${sourceFingerprint(directStreamIdentity(stream.externalUrl, stream))}`
  if (stream.ytId) return `youtube:${sourceFingerprint(stream.ytId)}`
  return `title:${sourceFingerprint(JSON.stringify([
    stream.name || '',
    stream.title || stream.description || '',
    stream.behaviorHints?.filename || '',
  ]))}`
}

/** Exact source choices cross a relay that may briefly queue commands. Use a
 * stable opaque identifier rather than sending a signed media URL or a long
 * release payload through it. Two independent 32-bit hashes plus the input
 * length make accidental collisions within one source list vanishingly small. */
function sourceFingerprint(value: string) {
  let first = 0x811c9dc5
  let second = 0x9e3779b9
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    first = Math.imul(first ^ code, 0x01000193)
    second = Math.imul(second ^ code, 0x85ebca6b)
  }
  return `${(first >>> 0).toString(36)}${(second >>> 0).toString(36)}${value.length.toString(36)}`
}

function directStreamIdentity(url: string, stream: StremioStream) {
  let stableUrl = url.replace(/[?#].*$/, '')
  try {
    const parsed = new URL(url)
    stableUrl = `${parsed.origin}${parsed.pathname}`
  } catch { /* keep the query-free fallback */ }
  return JSON.stringify([
    stableUrl,
    stream.behaviorHints?.filename || '',
    stream.name || '',
    stream.title || stream.description || '',
    stream.behaviorHints?.videoSize || 0,
  ])
}

export function makeCommandEnvelope(command: RemoteCommand, id?: string): RemoteCommandEnvelope {
  return {
    v: REMOTE_PROTOCOL_VERSION,
    type: 'command',
    id: id || `${Date.now().toString(36)}-${cryptoRandomId()}`,
    command,
    sentAt: Date.now(),
  }
}

function cryptoRandomId() {
  const bytes = new Uint8Array(9)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}
